import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, apiRequest } from '../api/client.js';
import type { ConnectionEntry, PlatformName } from '../api/types.js';
import { Alert } from '../components/Alert.js';
import { Field } from '../components/Field.js';
import { PlatformIcon } from '../components/PlatformIcon.js';
import { DiscordChannelsDialog } from '../components/DiscordChannelsDialog.js';
import { DisconnectDialog } from '../components/DisconnectDialog.js';
import { PlatformHelpDialog } from '../components/PlatformHelpDialog.js';
import { hasHelp } from '../components/platform-help.js';
import { StatusPill } from '../components/StatusPill.js';

/**
 * Connections (ARCHITECTURE.md §64, §15).
 *
 * The platform list comes from the API, which owns the catalog. The Console
 * keeps no list of its own: §18 requires that Connaro never claim unvalidated
 * platform support, and that rule can only hold if exactly one place decides
 * what is supported.
 *
 * `connectable` is likewise computed server-side from whether the adapter
 * exists AND whether the actor holds `platforms.connect`. The button state is
 * a rendering of that answer, not an opinion.
 *
 * Connecting is a POST that RETURNS an authorization URL rather than a link the
 * Console builds. Only the API knows the client id and the registered redirect
 * URI, and only the API can mint the short-lived state bound to this user,
 * tenant and platform (§27). A URL assembled in the browser could not be bound
 * to anything, which is the whole point of the state parameter.
 */

/** Outcomes the OAuth callback redirects back with. */
const CONNECT_OUTCOMES: Record<string, { tone: 'success' | 'warning' | 'error'; message: string }> =
  {
    success: { tone: 'success', message: 'Connected. Connaro is establishing the connection now.' },
    denied: {
      tone: 'warning',
      message: 'Authorization was cancelled, so nothing was connected.',
    },
    /*
     * The plan's ceiling, reached. Deliberately not an error tone: the
     * operator did nothing wrong and nothing is broken — the answer is a plan
     * decision, not a retry.
     */
    limit: {
      tone: 'warning',
      message:
        'This workspace has connected as many platforms as its plan includes. Disconnect one, or upgrade to add more.',
    },
    invalid: {
      tone: 'error',
      message:
        'That authorization could not be verified and was rejected. Please start the connection again.',
    },
  };
export function ConnectionsPage(): React.JSX.Element {
  const [entries, setEntries] = useState<ConnectionEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [channelsOpen, setChannelsOpen] = useState(false);
  /** The connection awaiting confirmation, or null when nothing is. */
  const [disconnecting, setDisconnecting] = useState<{
    connectionId: string;
    platform: PlatformName;
    displayName: string;
  } | null>(null);
  const [helpFor, setHelpFor] = useState<{ platform: PlatformName; displayName: string } | null>(
    null,
  );
  /** Platform whose authorization is in flight, so the button cannot double-fire. */
  const [starting, setStarting] = useState<PlatformName | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  /*
   * Rumble's pasted URL, and its own error slot.
   *
   * Kept apart from `startError` because the two are shown in different places:
   * `startError` is a page-level alert about an authorization that failed to
   * start, while this belongs under the field the operator just typed into.
   */
  const [rumbleUrl, setRumbleUrl] = useState('');
  const [rumbleError, setRumbleError] = useState<string | null>(null);

  /*
   * Whether to show the field on an ALREADY-connected Rumble card.
   *
   * A connected card must stop asking — an empty "paste your URL" box under an
   * ACTIVE status reads as though the connection never took. But Rumble lets a
   * customer reset the URL and revoke the old one at any time, so a rotated key
   * still needs somewhere to go. Hence a deliberate reveal rather than either
   * always showing the field or removing it outright.
   */
  const [replacingRumble, setReplacingRumble] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  /*
   * Captured on the FIRST render, then the URL is cleaned below.
   *
   * Reading it straight from `searchParams` would make the banner vanish the
   * instant the parameter was stripped — a one-frame flash the operator never
   * sees. The message and the URL are separate concerns: the message should
   * persist for this visit, the parameter should not survive a refresh.
   */
  const [outcomeKey] = useState(() => searchParams.get('connect') ?? '');
  const outcome = CONNECT_OUTCOMES[outcomeKey];

  const load = useCallback(async (): Promise<ConnectionEntry[] | undefined> => {
    try {
      const response = await apiRequest<{ connections: ConnectionEntry[] }>('/connections');
      setEntries(response.connections);
      return response.connections;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load connections.');
      return undefined;
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Settle a freshly-connected platform without a manual reload.
   *
   * The OAuth callback writes PENDING, and connaro-worker moves it to ACTIVE
   * when it picks the connection up — up to one poll interval later. The page
   * fetched once, so it showed "Pending" indefinitely and looked stuck.
   *
   * This polls only while something is genuinely mid-transition, and stops as
   * soon as nothing is. It is NOT a general auto-refresh: a settled page makes
   * no requests at all.
   */
  useEffect(() => {
    if (entries === null) return;

    const settling = entries.some(
      (entry) =>
        entry.connection?.status === 'PENDING' || entry.connection?.status === 'CONNECTING',
    );

    if (!settling) return;

    const timer = setInterval(() => {
      void load();
    }, 3_000);

    /*
     * A hard stop. If a connection stays PENDING because the worker is down,
     * polling forever would be a slow leak against the API for a page someone
     * left open — and the status shown would be accurate anyway.
     */
    const giveUp = setTimeout(() => {
      clearInterval(timer);
    }, 60_000);

    return () => {
      clearInterval(timer);
      clearTimeout(giveUp);
    };
  }, [entries, load]);

  /*
   * Clear the outcome from the URL once it has been shown.
   *
   * Otherwise a refresh or a shared link would replay "Connected" long after
   * the fact, and a bookmarked URL would claim a connection that may since have
   * been removed.
   */
  useEffect(() => {
    if (!searchParams.has('connect')) return;

    const next = new URLSearchParams(searchParams);
    next.delete('connect');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const connect = useCallback(async (platform: PlatformName): Promise<void> => {
    setStarting(platform);
    setStartError(null);

    try {
      const { authorizationUrl } = await apiRequest<{ authorizationUrl: string }>(
        `/oauth/${platform}/start`,
        { method: 'POST', body: { redirectPath: '/connections' } },
      );

      // A full navigation, not a fetch: the provider's consent screen is a page
      // the operator has to see and interact with.
      window.location.assign(authorizationUrl);
    } catch (caught) {
      setStarting(null);
      setStartError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not start authorization. Please try again.',
      );
    }
  }, []);

  /**
   * Connect Rumble from a pasted URL.
   *
   * Deliberately NOT the flow above. Rumble authenticates nothing — there is no
   * client id, no consent screen and no authorization to start — so the OAuth
   * route rejects it outright. What the operator has instead is a URL Rumble
   * generated for them, which they paste here.
   *
   * Two consequences follow, and both differ from every other card. There is no
   * navigation away, so the list is reloaded in place rather than the page
   * being replaced. And the field is cleared on success: the pasted value is a
   * live stream key, not a read-only token, and leaving it on screen keeps a
   * broadcast credential visible for as long as the tab is open.
   */
  const connectRumble = useCallback(async (): Promise<void> => {
    setStarting('rumble');
    setRumbleError(null);

    try {
      await apiRequest<{ connectionId: string; status: string }>('/connections/rumble', {
        method: 'POST',
        body: { apiUrl: rumbleUrl },
      });

      setRumbleUrl('');
      // Close the reveal, so a replaced URL returns the card to its connected
      // state rather than leaving the field open asking for another one.
      setReplacingRumble(false);
      await load();
    } catch (caught) {
      /*
       * The API's message is safe to show: §49 forbids credentials in error
       * text, and the connect route's validation errors never echo the URL they
       * rejected — which matters here, because the thing rejected is a secret.
       */
      setRumbleError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not connect Rumble. Please check the URL and try again.',
      );
    } finally {
      setStarting(null);
    }
  }, [load, rumbleUrl]);

  if (error !== null) {
    return (
      <section className="panel">
        <div className="panel__body">
          <Alert tone="error">{error}</Alert>
        </div>
      </section>
    );
  }

  if (entries === null) {
    return (
      <p role="status" aria-live="polite">
        Loading connections…
      </p>
    );
  }

  /*
   * What an operator can actually DO, which is not the same as what is
   * "available". `available` is §18's customer-facing claim of validated
   * support; `connectable` is whether this deployment has credentials and the
   * actor holds the permission. A workspace with four working connections was
   * previously told "nothing here can be connected today" because only Twitch
   * had reached `available`.
   */
  const connectableCount = entries.filter((entry) => entry.connectable).length;
  const connectedCount = entries.filter((entry) => entry.connection !== null).length;

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">Connections</h1>
        <p className="page__subtitle">
          Streaming platforms linked to this workspace. Connections belong to the workspace, not to
          you personally.
        </p>
      </header>

      {outcome === undefined ? null : <Alert tone={outcome.tone}>{outcome.message}</Alert>}

      {startError === null ? null : <Alert tone="error">{startError}</Alert>}

      {connectableCount === 0 ? (
        <div className="alert alert--info" role="status">
          No platform can be connected from this deployment yet. Connaro speaks only to official,
          documented APIs, and each platform needs its application credentials configured before it
          will appear as connectable here.
        </div>
      ) : (
        /*
         * An orientation line rather than a warning. Counts are stated in words
         * as well as shown by the cards, because a grid of six is exactly where
         * someone loses track of what is actually running.
         */
        <p className="page__hint" role="status">
          {connectedCount === 0
            ? `${String(connectableCount)} platform${connectableCount === 1 ? '' : 's'} ready to connect. Pick one below — each card has setup instructions.`
            : `${String(connectedCount)} connected, ${String(connectableCount)} available to connect. Each card has setup instructions.`}
        </p>
      )}

      <div className="grid grid--2">
        {entries.map((entry) => (
          <section
            className={`panel platform-card platform-card--${entry.platform}`}
            key={entry.platform}
            aria-labelledby={`platform-${entry.platform}`}
          >
            <div className="panel__header">
              <h2 className="panel__title platform-title" id={`platform-${entry.platform}`}>
                <PlatformIcon platform={entry.platform} displayName={entry.displayName} />
                {entry.displayName}
                {hasHelp(entry.platform) ? (
                  <button
                    type="button"
                    className="help-hint"
                    aria-label={`Setup instructions for ${entry.displayName}`}
                    onClick={() => {
                      setHelpFor({ platform: entry.platform, displayName: entry.displayName });
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="9"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M9.5 9a2.5 2.5 0 1 1 3.2 2.4c-.6.2-1 .8-1 1.4v.4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
                    </svg>
                  </button>
                ) : null}
              </h2>
              {entry.connection !== null ? (
                <StatusPill tone={entry.connection.tone} label={entry.connection.label} />
              ) : (
                /*
                 * Three states, because there are three answers.
                 *
                 * This used to read "Unavailable" whenever `connectable` was
                 * false, which put a built-but-unconfigured platform in the
                 * same words as one with no adapter at all. Instagram showed
                 * "Unavailable" above a note reading "Built and pending
                 * validation" — the card contradicting itself.
                 *
                 * `availability` comes from the server and does not depend on
                 * the actor, so a viewer of a working platform sees "Not
                 * connected" rather than being told the deployment is broken.
                 */
                <StatusPill
                  tone="idle"
                  label={
                    entry.availability === 'unsupported'
                      ? 'Unavailable'
                      : entry.availability === 'needs_setup'
                        ? 'Needs setup'
                        : 'Not connected'
                  }
                />
              )}
            </div>

            <div className="panel__body">
              <p style={{ margin: '0 0 var(--s-2)', fontSize: 'var(--text-sm)' }}>
                {entry.summary}
              </p>
              <p
                style={{
                  margin: '0 0 var(--s-4)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--c-text-muted)',
                }}
              >
                {entry.note}
              </p>

              {entry.connection?.account?.displayName === undefined ||
              entry.connection.account.displayName === null ? null : (
                <p style={{ margin: '0 0 var(--s-4)', fontSize: 'var(--text-sm)' }}>
                  Connected as <strong>{entry.connection.account.displayName}</strong>
                </p>
              )}

              {/*
               * Rumble gets a field where every other platform gets a button.
               *
               * It is the only provider with nothing to authorize against: no
               * client id, no consent screen, no redirect. The operator brings
               * a URL Rumble generated for them. Sending them through
               * `/oauth/rumble/start` instead would fail every time, which is
               * exactly what this card used to do.
               */}
              {entry.platform === 'rumble' &&
              entry.connectable &&
              entry.connection !== null &&
              !replacingRumble ? (
                /*
                 * Connected, so the card stops asking. `connectable` alone was
                 * the wrong gate: it stays true once a platform is connected,
                 * so an ACTIVE card kept showing an empty "paste your URL" box
                 * as though nothing had been entered.
                 */
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setRumbleError(null);
                    setReplacingRumble(true);
                  }}
                >
                  Replace API URL
                </button>
              ) : entry.platform === 'rumble' && entry.connectable ? (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void connectRumble();
                  }}
                >
                  {/*
                   * The shared `Field`, not hand-rolled markup. It gives the
                   * error a stable id and wires it into `aria-describedby`, so
                   * the reason stays reachable FROM the input rather than only
                   * being announced once when it appears. Rebuilding that by
                   * hand is exactly how the wiring goes missing.
                   *
                   * `text` rather than `url` is deliberate: the server checks
                   * the scheme and the host and says which one failed, where
                   * browser validation would reject the paste first, in wording
                   * nobody here chose.
                   */}
                  <Field
                    label="Rumble Live Stream API URL"
                    hint="Generate this on the Live Stream API page in your Rumble account settings. It contains your live stream key, so treat it the way you would a password."
                    value={rumbleUrl}
                    onChange={setRumbleUrl}
                    error={rumbleError ?? undefined}
                    disabled={starting !== null}
                    autoComplete="off"
                    spellCheck={false}
                  />

                  <button
                    type="submit"
                    className="btn"
                    disabled={rumbleUrl.trim() === '' || starting !== null}
                  >
                    {starting === 'rumble'
                      ? 'Connecting Rumble…'
                      : `${entry.connection === null ? 'Connect' : 'Reconnect'} Rumble`}
                  </button>
                </form>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn"
                    disabled={!entry.connectable || starting !== null}
                    aria-describedby={`why-${entry.platform}`}
                    onClick={() => {
                      void connect(entry.platform);
                    }}
                  >
                    {starting === entry.platform
                      ? `Redirecting to ${entry.displayName}…`
                      : `${entry.connection === null ? 'Connect' : 'Reconnect'} ${entry.displayName}`}
                  </button>
                  <span className="visually-hidden" id={`why-${entry.platform}`}>
                    {/*
                     * The disabled button's reason, and it must match the pill.
                     * It previously said "not available yet" for every disabled
                     * card, which is the same conflation — and the one place a
                     * screen reader user gets the explanation at all.
                     */}
                    {entry.connectable
                      ? `Connect your ${entry.displayName} account.`
                      : entry.availability === 'needs_setup'
                        ? `${entry.displayName} is supported, but this Connaro deployment has no ${entry.displayName} application credentials configured yet.`
                        : entry.availability === 'ready'
                          ? `You do not have permission to connect ${entry.displayName}.`
                          : `${entry.displayName} integration is not available yet.`}
                  </span>
                </>
              )}

              {/*
               * Discord alone has channels to choose, because it is the only
               * provider whose unit of authorization is a server rather than an
               * account (§96). The control belongs on its own card.
               */}
              {entry.platform === 'discord' && entry.connection !== null ? (
                <button
                  type="button"
                  className="btn btn--secondary"
                  style={{ marginLeft: 'var(--s-2)' }}
                  onClick={() => {
                    setChannelsOpen(true);
                  }}
                >
                  Choose channels
                </button>
              ) : null}

              {/*
               * Disconnect, on every connected card whose actor may do it.
               *
               * `disconnectable` is the server's answer, not a guess from
               * `connection !== null`: a VIEWER can see the connection and must
               * not be shown a control that would 403. It is deliberately not
               * gated on `connectable` — a deployment that has since dropped a
               * platform's credentials must still let a workspace let go of
               * what it holds.
               *
               * Secondary styling, not danger. The destructive framing belongs
               * in the confirmation, where the consequences are actually
               * stated; a red button on every connected card reads as an alarm
               * about the connection rather than an action available on it.
               */}
              {entry.disconnectable && entry.connection !== null ? (
                <button
                  type="button"
                  className="btn btn--secondary"
                  style={{ marginLeft: 'var(--s-2)' }}
                  onClick={() => {
                    setDisconnecting({
                      connectionId: entry.connection?.id ?? '',
                      platform: entry.platform,
                      displayName: entry.displayName,
                    });
                  }}
                >
                  Disconnect
                </button>
              ) : null}
            </div>
          </section>
        ))}
      </div>

      {/*
       * The channel picker is a dialog opened from the Discord card, not a
       * panel of its own: panels in this area are one per platform, and a
       * second kind among them reads as another integration rather than as
       * settings belonging to one.
       */}
      <PlatformHelpDialog
        platform={helpFor?.platform ?? null}
        displayName={helpFor?.displayName ?? ''}
        onClose={() => {
          setHelpFor(null);
        }}
      />

      <DiscordChannelsDialog
        open={channelsOpen}
        onClose={() => {
          setChannelsOpen(false);
        }}
      />

      <DisconnectDialog
        target={disconnecting}
        onClose={() => {
          setDisconnecting(null);
        }}
        onDisconnected={async () => {
          await load();
        }}
      />
    </>
  );
}
